/**
 * Pure-logic helpers for the TitleScene save-slot card (PR 5 of the
 * Future Cartridge migration — TODOs #24).
 *
 * getSlotSummary projects the live runState + careerStore into the
 * minimal shape the title-screen DOM overlays need. Dependency-injected
 * so the unit tests don't touch the singletons (matches the
 * dialogueOverlayLogic / followerState split pattern).
 *
 * mapDisplayName resolves a converter map id (e.g. 'PewterCity_Gym')
 * down to a human region label ('Pewter City'). Cartridge convention:
 * interiors collapse to the parent region rather than surface their own
 * label — the slot card has one cell for "Region" and "Pewter Gym" vs
 * "Pewter City" is not a distinction the player cares about at the
 * title screen.
 *
 * No consumer wires these yet — the next commit (PR 5 behavioral)
 * mounts the DOM overlay and reads them.
 */

import type { PartyMember } from '../state/runState';

/** Lead-row data — the headline element of the slot card. Null when no
 *  party exists (empty-save state). */
export interface SlotSummaryLead {
  species: string;
  level: number;
  hpCurrent: number;
  hpMax: number;
}

/** Mini-orb data, one per party slot. The slot card always renders six
 *  cells; null pads empty slots so the layout doesn't shift between a
 *  party of 2 and a party of 6. */
export interface SlotSummaryPartyMember {
  species: string;
  /** False when hp.current === 0. Doesn't happen in practice between
   *  battles (BattleScene splices fainted slots on battleEnd) but the
   *  alive flag is the defensive read for any save written mid-faint
   *  via pagehide. */
  alive: boolean;
}

export interface SlotSummary {
  /** True iff a party exists. Drives the slot-card vs new-run-only
   *  branching in TitleScene. localStorage is NOT consulted directly —
   *  saveManager.load() already restored runState at module init, so
   *  party-length is the authoritative "has progress" signal. */
  hasSave: boolean;
  lead: SlotSummaryLead | null;
  /** Always length 6; null pads empty slots. */
  partyMembers: Array<SlotSummaryPartyMember | null>;
  /** Distinct species drafted this run. Drives the "This run" stat
   *  cell. Reads runState.getDraftedSpecies().size. */
  drafted: number;
  /** Career totals across all runs. Drives the "Lifetime" stat cell. */
  total: { drafts: number; deaths: number };
  /** Human region label, resolved via mapDisplayName. '—' when no
   *  position is recorded yet (legacy v5 saves migrated to null
   *  position; fresh saves on the first map step write a real value). */
  region: string;
  /** Lead's hp.current/hp.max < 25%. Drives the lead row's HP-value
   *  red color. */
  criticalLead: boolean;
  /** ANY alive party member at < 25% HP. Drives the slot card's amber
   *  specular-edge border. Two distinct signals per spec:
   *    red lead HP value = lead is critical specifically
   *    amber border      = at least one member is critical (incl. lead) */
  criticalAny: boolean;
}

/** Accessor surface for getSlotSummary. Each accessor returns a plain
 *  value snapshot — no listeners, no async. The TitleScene supplies
 *  these as direct method references at call time so re-renders see
 *  the latest values. */
export interface SlotDeps {
  getParty: () => readonly PartyMember[];
  /** Only mapName is read; loosened from PlayerPosition so tests can
   *  pass partial position objects without satisfying tileX/tileY/
   *  facing. PlayerPosition is assignable to this shape. */
  getPosition: () => { mapName: string } | null;
  getDraftedSpecies: () => ReadonlySet<string>;
  getTotals: () => { drafts: number; deaths: number };
}

const CRITICAL_HP_RATIO = 0.25;
const PARTY_SLOT_COUNT = 6;

/** Project live state into the title slot summary. Pure function of the
 *  injected accessors; safe to call repeatedly. */
export function getSlotSummary(deps: SlotDeps): SlotSummary {
  const party = deps.getParty();
  const hasSave = party.length > 0;

  const leadMember = hasSave ? party[0] : undefined;
  const lead: SlotSummaryLead | null = leadMember
    ? {
        species: leadMember.species,
        level: leadMember.level,
        hpCurrent: leadMember.hp.current,
        hpMax: leadMember.hp.max,
      }
    : null;

  const partyMembers: Array<SlotSummaryPartyMember | null> = [];
  for (let i = 0; i < PARTY_SLOT_COUNT; i++) {
    const m = party[i];
    partyMembers.push(m ? { species: m.species, alive: m.hp.current > 0 } : null);
  }

  const drafted = deps.getDraftedSpecies().size;
  const total = deps.getTotals();
  const position = deps.getPosition();
  const region = mapDisplayName(position?.mapName);

  const criticalLead = lead ? lead.hpCurrent / lead.hpMax < CRITICAL_HP_RATIO : false;
  // criticalAny filters by alive (hp.current > 0) so a fainted slot
  // caught mid-flush doesn't trigger the amber border. In practice
  // fainted slots are spliced at battleEnd before any TitleScene mount
  // would observe them; this is defense-in-depth.
  const criticalAny = party.some(
    (m) => m.hp.current > 0 && m.hp.current / m.hp.max < CRITICAL_HP_RATIO,
  );

  return { hasSave, lead, partyMembers, drafted, total, region, criticalLead, criticalAny };
}

/** Converter map id → human region label. Interiors collapse to the
 *  parent region (cartridge convention — gym/center sub-floors don't
 *  earn their own slot-card label). Unknown ids pass through verbatim
 *  rather than throwing — defensive against converter additions that
 *  outrun this table. The em-dash sentinel only applies to null/
 *  undefined; an empty string would still pass through to fallback. */
const MAP_DISPLAY_NAMES: Record<string, string> = {
  PalletTown: 'Pallet Town',
  PalletTown_PlayersHouse_1F: 'Pallet Town',
  PalletTown_ProfessorOaksLab: 'Pallet Town',
  Route1: 'Route 1',
  ViridianCity: 'Viridian City',
  ViridianForest: 'Viridian Forest',
  Route2: 'Route 2',
  Route2_ViridianForest_NorthEntrance: 'Route 2',
  Route2_ViridianForest_SouthEntrance: 'Route 2',
  PewterCity: 'Pewter City',
  PewterCity_Gym: 'Pewter City',
  PewterCity_PokemonCenter_1F: 'Pewter City',
};

export function mapDisplayName(mapId: string | null | undefined): string {
  if (!mapId) return '—';
  return MAP_DISPLAY_NAMES[mapId] ?? mapId;
}
